import { usePromisedRequest } from "../Common/request";
import { resolveWithBody } from "./Utils/utils";
import { BracketType, User } from "../Common/types";
import { VerificationState } from "./verifications";
import { FightResult, KataResult } from "../Common/Fights/types";

export type LogType =
    | "paymentUpdated"
    | "eventRegistrationUpdate"
    | "competitorCallIncreased"
    | "verificationStatusChanged"
    | "fightFinished"
    | "fightResultReverted";

export type FightRevertReason = "organizer" | "assigningCompetitorToByeSpot";

export type PaymentUpdated = {
    eventRegistrationId: number;
    firstName: string;
    lastName: string;
    academyName: string;
    branch: string;
    complete: boolean;
    amount: number;
    paymentStatus: string;
    discountPercentage?: number;
    logType: Extract<LogType, "paymentUpdated">;
};

export type EventRegistrationUpdate = {
    logType: Extract<LogType, "eventRegistrationUpdate">;
    eventRegistrationId: number;
    firstName: string;
    lastName: string;
    academyName: string;
    branch: string;
    categoryName: string;
    action: string;
};

export type CompetitorCallIncreased = {
    logType: Extract<LogType, "competitorCallIncreased">;
    eventRegistrationId: number;
    calledCompetitorFirstName: string;
    calledCompetitorLastName: string;
    calledCompetitorAcademy: string;
    calledCompetitorBranch: string;
    fightId: number;
    fightNumber: number | null;
    fightRound: string;
    bracketLevel: number;
    categoryName: string;
    firstCompetitorName: string;
    secondCompetitorName: string;
    callCount: number;
};

export type VerificationStatusChanged = {
    logType: Extract<LogType, "verificationStatusChanged">;
    eventRegistrationId: number;
    competitorFirstName: string;
    competitorLastName: string;
    competitorAcademy: string;
    categoryName: string;
    previousStatus: VerificationState;
    newStatus: VerificationState;
};

export type FightFinished = {
    logType: Extract<LogType, "fightFinished">;
    fightId: number;
    fightNumber: number | null;
    fightRound: string;
    bracketLevel: number;
    bracketType: BracketType;
    categoryName: string;
    matId: number | null;
    matName: string;
    firstEventRegistrationId: number | null;
    firstCompetitorName: string;
    secondEventRegistrationId: number | null;
    secondCompetitorName: string;
    winnerCompetitorName: string;
    result: FightResult | KataResult;
};

export type FightResultReverted = {
    logType: Extract<LogType, "fightResultReverted">;
    fightId: number;
    fightNumber: number | null;
    fightRound: string;
    bracketLevel: number;
    bracketType: BracketType;
    categoryName: string;
    matId: number | null;
    matName: string;
    firstEventRegistrationId: number | null;
    firstCompetitorName: string;
    secondEventRegistrationId: number | null;
    secondCompetitorName: string;
    reason: FightRevertReason;
};

export type AuditLog = {
    id: number;
    eventId: number;
    userId: number;
    logType: LogType;
    context:
        | PaymentUpdated
        | EventRegistrationUpdate
        | CompetitorCallIncreased
        | VerificationStatusChanged
        | FightFinished
        | FightResultReverted;
    created: string;
};

export type FullAuditLogEntry = AuditLog & {
    user: User;
};

export type AuditLogListingRequest = {
    query: string;
    limit: number;
    offset: number;
    logType: LogType | null;
};

export default {
    getAuditLogsListing(eventId: number, request: AuditLogListingRequest): Promise<FullAuditLogEntry[]> {
        let url = `/api/admin/events/${eventId}/audit-logs`;
        const urlSearchParams = new URLSearchParams();
        if (request.query) {
            urlSearchParams.set("query", request.query);
        }
        if (request.limit) {
            urlSearchParams.set("limit", request.limit.toString());
        }
        if (request.offset) {
            urlSearchParams.set("offset", request.offset.toString());
        }
        if (request.logType) {
            urlSearchParams.set("logType", request.logType);
        }
        if (urlSearchParams.toString()) {
            url += `?${urlSearchParams.toString()}`;
        }

        return usePromisedRequest<FullAuditLogEntry[]>({
            url: url,
            json: true,
            method: "get",
        }).then(resolveWithBody());
    },
};
